import {
  apiClient,
} from "./apiClient";

import type {
  Advertisement,
  AdvertisementDeleteResult,
  AdvertisementsListData,
  AdvertisementsListQuery,
  CreateAdvertisementInput,
  UpdateAdvertisementInput,
} from "../types/advertisement.types";

function buildQuery(
  query: AdvertisementsListQuery = {},
): string {
  const params =
    new URLSearchParams();

  if (query.status) {
    params.set(
      "status",
      query.status,
    );
  }

  if (query.placement) {
    params.set(
      "placement",
      query.placement,
    );
  }

  if (query.contentType) {
    params.set(
      "contentType",
      query.contentType,
    );
  }

  if (query.limit) {
    params.set(
      "limit",
      String(
        query.limit,
      ),
    );
  }

  if (query.cursor) {
    params.set(
      "cursor",
      query.cursor,
    );
  }

  const value =
    params.toString();

  return value
    ? `?${value}`
    : "";
}

function createImageFormData(
  file: File,
) {
  const formData =
    new FormData();

  /**
   * Must match:
   *
   * advertisementImageUpload.single('image')
   */
  formData.append(
    "image",
    file,
  );

  return formData;
}

export const advertisementsApi = {
  list(
    query:
      AdvertisementsListQuery = {},
  ) {
    return apiClient.get<
      AdvertisementsListData
    >(
      `/advertisements${buildQuery(
        query,
      )}`,
    );
  },

  getById(
    advertisementId: string,
  ) {
    return apiClient.get<
      Advertisement
    >(
      `/advertisements/${encodeURIComponent(
        advertisementId,
      )}`,
    );
  },

  create(
    input:
      CreateAdvertisementInput,
  ) {
    return apiClient.post<
      Advertisement
    >(
      "/advertisements",
      input,
    );
  },

  update(
    advertisementId: string,

    input:
      UpdateAdvertisementInput,
  ) {
    return apiClient.patch<
      Advertisement
    >(
      `/advertisements/${encodeURIComponent(
        advertisementId,
      )}`,

      input,
    );
  },

  uploadMainImage(
    advertisementId: string,

    file: File,
  ) {
    return apiClient.post<
      Advertisement
    >(
      `/advertisements/${encodeURIComponent(
        advertisementId,
      )}/media/main`,

      createImageFormData(
        file,
      ),
    );
  },

  uploadMobileImage(
    advertisementId: string,

    file: File,
  ) {
    return apiClient.post<
      Advertisement
    >(
      `/advertisements/${encodeURIComponent(
        advertisementId,
      )}/media/mobile`,

      createImageFormData(
        file,
      ),
    );
  },

  deleteMainImage(
    advertisementId: string,
  ) {
    return apiClient.delete<
      Advertisement
    >(
      `/advertisements/${encodeURIComponent(
        advertisementId,
      )}/media/main`,
    );
  },

  deleteMobileImage(
    advertisementId: string,
  ) {
    return apiClient.delete<
      Advertisement
    >(
      `/advertisements/${encodeURIComponent(
        advertisementId,
      )}/media/mobile`,
    );
  },

  publish(
    advertisementId: string,
  ) {
    return apiClient.post<
      Advertisement
    >(
      `/advertisements/${encodeURIComponent(
        advertisementId,
      )}/publish`,
    );
  },

  pause(
    advertisementId: string,
  ) {
    return apiClient.post<
      Advertisement
    >(
      `/advertisements/${encodeURIComponent(
        advertisementId,
      )}/pause`,
    );
  },

  archive(
    advertisementId: string,
  ) {
    return apiClient.delete<
      AdvertisementDeleteResult
    >(
      `/advertisements/${encodeURIComponent(
        advertisementId,
      )}`,
    );
  },
};